import { v4 as uuidv4 } from 'uuid';
import { Select, message } from 'antd';
import React, { Component } from 'react';
import { api } from '$app/api';
import actionDefinitions from './actionDefinitions';
import { serviceSheetBlocksWorkComplete, submitMarkWorkComplete } from '../../ServiceSheetMissingAlert/logic';
import ServiceSheetMissingModal from '../../ServiceSheetMissingAlert';
import { StatusSelect } from './styled';

import { BOOKING_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import getBookingStatusName from '../../../../shared/getBookingStatusName';
import Modal from '../../../../components/Modal';
import BookingPageContext from '../../context';

const { Option } = Select;

const isBookingCancelledWithoutReason = (payload) =>
  (payload.ADD_NOTE && payload.ADD_NOTE.noteType === 'BookingCancelled' && !payload.ADD_NOTE.content) ||
  (payload.CANCEL_BOOKING &&
    payload.CANCEL_BOOKING.note.noteType === 'BookingCancelled' &&
    !payload.CANCEL_BOOKING.note.content);

class BookingActions extends Component {
  static contextType = BookingPageContext;

  constructor(props) {
    super(props);
    this.actionsStores = {};
    this.state = {
      modalType: null,
      isModalBusy: false,
    };
  }

  handleChange = (value) => {
    this.setState({ modalType: value });
  };

  get modal() {
    return actionDefinitions.find(({ type }) => type === this.state.modalType) || null;
  }

  markWorkComplete = () => {
    const { booking } = this.props;
    return submitMarkWorkComplete({
      booking,
      patch: () => api.patch(`/bookings/${booking.bookingId}`, { masterStatusId: 10700 }),
      notifyError: (text) => message.error(text),
      reloadBooking: () => this.context.reloadBooking(),
    });
  };

  confirm = async () => {
    const { booking } = this.props;
    const modal = this.modal;

    const idempotencyKey = uuidv4();
    const modalPayload = modal.payload(this.actionsStores, booking, idempotencyKey);

    if (isBookingCancelledWithoutReason(modalPayload)) {
      message.error('Please select cancellation reason');
      return;
    }

    this.setState({ isModalBusy: true });

    for (const key of Object.keys(modalPayload)) {
      if (key === 'CHARGE_PAYMENT' || key === 'CHARGE_INSTALMENT') {
        await this.props[key === 'CHARGE_PAYMENT' ? 'chargePayment' : 'chargeInstalment'](idempotencyKey);
      } else if (key === 'PATCH_BOOKING' && modalPayload[key].idempotencyKey) {
        const { idempotencyKey: patchIdempotencyKey, ...patchData } = modalPayload[key];
        await this.props.patchBooking(patchData, patchIdempotencyKey);
      } else if (key === 'PATCH_BOOKING') {
        await this.props.patchBooking(modalPayload[key]);
      } else if (key === 'ADD_NOTE') {
        await this.props.addNoteAction(modalPayload[key]);
      } else if (key === 'CANCEL_BOOKING') {
        const { patch, note, idempotencyKey: cancelIdempotencyKey } = modalPayload[key];
        await this.props.cancelBooking(patch, note, cancelIdempotencyKey);
      } else if (key === 'MARK_WORK_COMPLETE') {
        await this.markWorkComplete();
      } else {
        await this.props[key](modalPayload[key]);
      }
    }

    this.actionsStores = {};
    this.setState({ modalType: null, isModalBusy: false });
  };

  get status() {
    const { booking } = this.props;
    return getBookingStatusName(booking);
  }

  shouldDisplayMasterStatus() {
    const { booking } = this.props;
    const { masterStatusId } = booking;

    return masterStatusId >= BOOKING_STATUS.WORK_COMPLETE && masterStatusId < BOOKING_STATUS.CLOSED;
  }

  isBookingInJobAllocationReview() {
    const { booking } = this.props;
    const { masterStatusId } = booking;

    if (booking.allocationStatus !== 'review') {
      return false;
    }

    return masterStatusId >= BOOKING_STATUS.UNALLOCATED && masterStatusId < BOOKING_STATUS.ALLOCATED;
  }

  renderMasterStatus() {
    const { booking } = this.props;
    const { collectionDeliveryOption } = booking;
    const actions = actionDefinitions.filter((action) => action.canDisplay(booking));

    if (this.shouldDisplayMasterStatus()) {
      return null;
    }

    const renderText = (collectionDeliveryOption, action) => {
      if (collectionDeliveryOption === 'CustomerC&D') {
        if (action.type === 'move.garage') return 'Move to awaiting drop off';
        if (action.type === 'move.pickup') return 'Back to allocated';
      }
      return action.text;
    };

    return (
      <StatusSelect
        value={this.status}
        className={booking.masterStatus.masterStatusName}
        style={{ width: 240 }}
        disabled={!actions.length}
        showArrow={actions.length > 0}
        onChange={this.handleChange}
      >
        {actions.map((action) => (
          <Option key={action.type} value={action.type}>
            {renderText(collectionDeliveryOption, action)}
          </Option>
        ))}
      </StatusSelect>
    );
  }

  renderTransportStatus() {
    const { booking } = this.props;
    const { collectionDeliveryOption } = booking;

    if (!this.shouldDisplayMasterStatus()) {
      return null;
    }

    const canTransitionedToDelivered = [
      'AWAITING_RETURN',
      'BEING_RETURNED',
      'BEING_RETURNED_HANDOVER',
      'TRANSPORT_TO_CUSTOMER',
      'TRANSPORT_TO_CUSTOMER_HANDOVER',
    ];

    const actions =
      canTransitionedToDelivered.indexOf(booking.transportStatus) >= 0
        ? [
          {
            type: 'DELIVERED',
            text: collectionDeliveryOption === 'CustomerC&D' ? 'Mark as returned' : 'Mark as delivered',
          },
        ]
        : [];

    let displayTransportStatus = booking.transportStatus;

    // this is to make the transition less confusion for cm, later this need to only display transport
    if (booking.transportStatus === 'AWAITING_RETURN') {
      displayTransportStatus = 'WORK_COMPLETE';
    } else if (booking.transportStatus === 'DELIVERED') {
      displayTransportStatus = 'RETURNED';
    }

    return (
      <StatusSelect
        value={displayTransportStatus}
        className={displayTransportStatus}
        style={{ width: 240 }}
        disabled={!actions.length}
        showArrow={actions.length > 0}
        onChange={(transportStatus) => this.props.patchBooking({ transportStatus })}
        id="transport-status-dropdown"
      >
        {actions.map((action) => (
          <Option key={action.type} value={action.type}>
            {action.text}
          </Option>
        ))}
      </StatusSelect>
    );
  }

  renderPaymentStatus() {
    const { booking } = this.props;
    const { paymentMethod } = booking;
    const actions = actionDefinitions.filter((action) => action.canDisplay(booking));

    if (!this.shouldDisplayMasterStatus()) {
      return null;
    }

    const mapStatusToImage = {
      failed: 'Payment failed',
      deleted: 'Payment failed',
      unpaid: 'Awaiting payment',
      locked: 'Locked',
      paid: 'Paid',
      processing: 'Processing',
    };
    const paymentStatusName = mapStatusToImage[paymentMethod.status];
    if (!paymentStatusName) {
      throw new Error(`renderPaymentStatus doesnt support status: ${paymentMethod.status}`);
    }
    const paymentStatusClass = paymentStatusName.toUpperCase().replace(/ /g, '_');

    return (
      <StatusSelect
        value={paymentStatusName}
        className={paymentStatusClass}
        style={{ width: 240 }}
        disabled={!actions.length || paymentMethod.status === 'locked'}
        showArrow={actions.length > 0}
        onChange={this.handleChange}
        id="booking-action-dropdown"
      >
        {actions.map((action) => (
          <Option key={action.type} value={action.type}>
            {action.text}
          </Option>
        ))}
      </StatusSelect>
    );
  }

  closeModal = () => {
    this.setState({ modalType: null, isModalBusy: false });
    this.actionsStores = {};
  };

  renderModal = () => {
    const { booking } = this.props;
    const modal = this.modal;

    if (!modal) return null;

    if (modal.type === 'work.complete' && serviceSheetBlocksWorkComplete(booking)) {
      return <ServiceSheetMissingModal visible onClose={this.closeModal} />;
    }

    return (
      <Modal
        // `Confirm cancellation of booking #${booking.bookingId}?`}
        title={modal && modal.title ? modal.title : 'Please confirm'}
        visible={!!modal}
        onOk={this.confirm}
        onCancel={this.closeModal}
        okText={modal && (modal.okText || 'Yes')}
        confirmLoading={this.state.isModalBusy}
        cancelText="No"
        className={modal && modal.className}
      >
        {modal.render(booking, this.actionsStores)}
      </Modal>
    );
  };

  render() {
    return (
      <div>
        {this.renderMasterStatus()}
        {this.renderTransportStatus()}
        {this.renderPaymentStatus()}
        {this.renderModal()}
      </div>
    );
  }
}

export default BookingActions;
